import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import DateRangeFilter from "./shared/DateRangeFilter";
import KpiCards from "./shared/KpiCards";
import AnalyticsChart from "./shared/AnalyticsChart";
import AnalyticsDataTable from "./shared/AnalyticsDataTable";
import AnalyticsMetricList from "./shared/AnalyticsMetricList";
import AnalyticsRankedAxisTick from "./shared/AnalyticsRankedAxisTick";
import AnalyticsWorkspaceHero from "./shared/AnalyticsWorkspaceHero";
import useAnalyticsDashboardData from "./shared/useAnalyticsDashboardData";
import {
  ANALYTICS_SERIES_COLORS,
  buildDistributionSummary,
  DistributionTooltip,
  formatDateValue,
  formatNumber,
  formatPercentage,
  formatRangeLabel,
  getKpiValue,
  getPeakEntry,
  getRangeDayCount,
  getTopItem,
  TimeSeriesTooltip,
} from "./shared/analyticsUtils";

function DoctorFeedbackSection(props) {
  const { data, loading, currentStartDate, currentEndDate, handleDateChange } = useAnalyticsDashboardData({
    dataUrl: props.dataUrl,
    startDate: props.startDate,
    endDate: props.endDate,
  });
  const locale = props.locale || "en";
  const labels = props.labels;
  const selectedRangeLabel = formatRangeLabel(currentStartDate, currentEndDate, locale);
  const rangeDayCount = getRangeDayCount(currentStartDate, currentEndDate);
  const volumeOverTime = data?.volume_over_time || [];
  const visibilityData = (data?.visibility_breakdown || []).map((item) => ({
    ...item,
    label: item.label === "true" || item.label === true ? labels.visible : labels.hidden,
  }));
  const topDoctors = data?.top_doctors || [];
  const recentFeedback = data?.recent_feedback || [];
  const totalFeedback = getKpiValue(data?.kpis, "total");
  const visibleFeedback = getKpiValue(data?.kpis, "visible");
  const visibilityRate = getKpiValue(data?.kpis, "visibility_rate");
  const averageDaily = rangeDayCount > 0 ? Math.round(totalFeedback / rangeDayCount) : 0;
  const peakDay = getPeakEntry(volumeOverTime);
  const leadingVisibility = getTopItem(visibilityData);
  const topDoctor = topDoctors[0];
  const loadingLabel = labels.loading || "...";

  const heroItems = [
    {
      label: labels.selectedWindow,
      value: selectedRangeLabel || loadingLabel,
    },
    {
      label: labels.totalInWindow,
      value: loading ? loadingLabel : formatNumber(totalFeedback, locale),
      hint: labels.feedbackUnit,
    },
    {
      label: labels.visibilityRate,
      value: loading ? loadingLabel : formatPercentage(visibilityRate, locale),
      hint: labels.visibilityRateHint,
    },
    {
      label: labels.topDoctor,
      value: loading ? loadingLabel : topDoctor?.name || labels.noData,
      hint: topDoctor ? `${formatNumber(topDoctor.value, locale)} ${labels.feedbackUnit}` : null,
    },
  ];

  const filterSummaryItems = [
    {
      label: labels.leadingVisibility,
      value: loading ? loadingLabel : leadingVisibility?.label || labels.noData,
      hint: leadingVisibility ? `${formatNumber(leadingVisibility.value, locale)} ${labels.feedbackUnit}` : null,
    },
    {
      label: labels.visibleFeedback,
      value: loading ? loadingLabel : formatNumber(visibleFeedback, locale),
      hint: labels.feedbackUnit,
    },
    {
      label: labels.topDoctor,
      value: loading ? loadingLabel : topDoctor?.name || labels.noData,
      hint: topDoctor ? `${formatNumber(topDoctor.value, locale)} ${labels.feedbackUnit}` : null,
    },
  ];

  const trendItems = [
    {
      label: labels.peakDay,
      value: peakDay ? formatNumber(peakDay.count, locale) : labels.noData,
      hint: peakDay ? formatDateValue(peakDay.date, locale, { day: "2-digit", month: "short", year: "numeric" }) : null,
    },
    {
      label: labels.averageDaily,
      value: formatNumber(averageDaily, locale),
      hint: labels.feedbackUnit,
    },
    {
      label: labels.visibleFeedback,
      value: formatNumber(visibleFeedback, locale),
      hint: labels.feedbackUnit,
    },
  ];

  const visibilitySummary = buildDistributionSummary(visibilityData, locale, labels.feedbackUnit);
  const doctorSummary = buildDistributionSummary(topDoctors, locale, labels.feedbackUnit, { labelKey: "name" });

  const columns = [
    { key: "doctor_name", label: labels.doctorName },
    { key: "account_name", label: labels.accountName },
    {
      key: "feedback",
      label: labels.feedbackText,
      tdClassName: "analytics-data-table__cell--wide",
      render: (row) => <span className="analytics-data-table__copy">{row.feedback}</span>,
    },
    {
      key: "visibility",
      label: labels.visibility,
      render: (row) => (
        <span className={`badge ${row.visibility ? "badge-success" : "badge-secondary"}`}>
          {row.visibility ? labels.visible : labels.hidden}
        </span>
      ),
    },
    { key: "created_at", label: labels.submittedAt },
  ];

  return (
    <div className="analytics-workspace">
      <AnalyticsWorkspaceHero
        eyebrow={labels.heroEyebrow}
        title={labels.heroTitle}
        description={labels.heroDescription}
        items={heroItems}
      />

      <DateRangeFilter
        startDate={currentStartDate}
        endDate={currentEndDate}
        labels={labels}
        onChange={handleDateChange}
        title={labels.filterTitle}
        description={labels.filterDescription}
        rangeLabel={selectedRangeLabel}
        summaryItems={filterSummaryItems}
      />

      <KpiCards kpis={data?.kpis} loading={loading} periodLabel={labels.vsPreviousPeriod} locale={locale} />

      <div className="row">
        <div className="col-xl-7 mb-4">
          <AnalyticsChart
            title={labels.volumeOverTime}
            description={labels.volumeOverTimeDescription}
            loading={loading}
            isEmpty={!volumeOverTime.length}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-chart"
            aside={<AnalyticsMetricList items={trendItems} emptyLabel={labels.noData} />}
            loadingHeight={320}
          >
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={volumeOverTime} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} strokeDasharray="4 4" stroke="rgba(148, 163, 184, 0.28)" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => formatDateValue(value, locale, { day: "2-digit", month: "short" })}
                  minTickGap={24}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                <Tooltip content={<TimeSeriesTooltip locale={locale} unitLabel={labels.feedbackUnit} />} />
                <Line type="monotone" dataKey="count" stroke="#0EA5E9" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </AnalyticsChart>
        </div>

        <div className="col-xl-5 mb-4">
          <AnalyticsChart
            title={labels.visibilityBreakdown}
            description={labels.visibilityBreakdownDescription}
            loading={loading}
            isEmpty={!visibilityData.length}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-eye"
            footer={<AnalyticsMetricList items={visibilitySummary} emptyLabel={labels.noData} compact={true} />}
            loadingHeight={320}
          >
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={visibilityData}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={72}
                  outerRadius={108}
                  paddingAngle={3}
                  stroke="none"
                >
                  {visibilityData.map((entry, index) => (
                    <Cell key={`${entry.label}-${index}`} fill={ANALYTICS_SERIES_COLORS[index % ANALYTICS_SERIES_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<DistributionTooltip locale={locale} unitLabel={labels.feedbackUnit} />} />
              </PieChart>
            </ResponsiveContainer>
          </AnalyticsChart>
        </div>
      </div>

      <div className="row">
        <div className="col-12 mb-4">
          <AnalyticsChart
            title={labels.topDoctors}
            description={labels.topDoctorsDescription}
            loading={loading}
            isEmpty={!topDoctors.length}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-account"
            footer={<AnalyticsMetricList items={doctorSummary} emptyLabel={labels.noData} compact={true} />}
            loadingHeight={340}
          >
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={topDoctors} layout="vertical" margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid horizontal strokeDasharray="4 4" stroke="rgba(148, 163, 184, 0.24)" />
                <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false} />
                <YAxis dataKey="name" type="category" width={180} tick={<AnalyticsRankedAxisTick maxLength={26} />} tickLine={false} axisLine={false} />
                <Tooltip content={<DistributionTooltip locale={locale} unitLabel={labels.feedbackUnit} labelKey="name" />} />
                <Bar dataKey="value" radius={[0, 10, 10, 0]}>
                  {topDoctors.map((entry, index) => (
                    <Cell key={`${entry.name}-${index}`} fill={ANALYTICS_SERIES_COLORS[index % ANALYTICS_SERIES_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </AnalyticsChart>
        </div>
      </div>

      <div className="row">
        <div className="col-12 mb-4">
          <AnalyticsDataTable
            title={labels.recentFeedback}
            description={labels.recentFeedbackDescription}
            loading={loading}
            rows={recentFeedback}
            columns={columns}
            noDataLabel={labels.noData}
            emptyIcon="zmdi-comment-edit"
            loadingHeight={240}
          />
        </div>
      </div>
    </div>
  );
}

export default DoctorFeedbackSection;
